import { Link } from 'react-router-dom';
import type { TVShow } from '@/mocks/tvShows';

interface TVShowCardProps {
  show: TVShow;
  variant?: 'default' | 'compact' | 'hero';
}

export default function TVShowCard({ show, variant = 'default' }: TVShowCardProps) {
  if (variant === 'hero') {
    return (
      <Link
        to={`/article/${show.id}`}
        className="block relative rounded-xl overflow-hidden group cursor-pointer h-[420px] md:h-[500px]"
      >
        <img
          src={show.image}
          alt={show.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-md whitespace-nowrap">
              {show.status}
            </span>
            <span className="px-2.5 py-1 bg-white/20 text-white text-xs font-medium rounded-md whitespace-nowrap">
              {show.category}
            </span>
            <span className="text-white/80 text-xs">{show.year}</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
            {show.title}
          </h2>
          <p className="text-white/70 text-sm line-clamp-2 max-w-xl">{show.description}</p>
          <div className="flex items-center gap-3 mt-4">
            <span className="flex items-center gap-1 text-accent-400 text-sm">
              <i className="ri-star-fill"></i>
              {show.rating}
            </span>
            <span className="text-white/60 text-sm">{show.updateInfo}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${show.id}`}
        className="flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
      >
        <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={show.image}
            alt={show.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
            {show.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-accent-500 text-xs flex items-center gap-0.5">
              <i className="ri-star-fill text-xs"></i>
              {show.rating}
            </span>
            <span className="text-foreground-400 text-xs">|</span>
            <span className="text-foreground-500 text-xs">{show.category}</span>
          </div>
          <p className="text-foreground-500 text-xs mt-1.5 line-clamp-2">{show.description}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${show.id}`}
      className="block group cursor-pointer"
    >
      <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-3">
        <img
          src={show.image}
          alt={show.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
          {show.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-white text-xs font-medium rounded whitespace-nowrap">
              新
            </span>
          )}
          {show.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded whitespace-nowrap">
              热播
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 bg-foreground-950/70 text-white text-xs rounded whitespace-nowrap">
            {show.rating}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
          <span className="text-white text-xs">{show.updateInfo}</span>
        </div>
      </div>
      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
        {show.title}
      </h3>
      <p className="text-foreground-500 text-xs mt-1">
        {show.actors.slice(0, 2).join(' / ')}
      </p>
    </Link>
  );
}